import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

export const createRepair = createAsyncThunk<
  any,
  {
    property_id: string;
    repair_title: string;
    area_type: string;
    area_name: string;
    status: string;
    category: string[];
    repair_images?: any[];
    estimate_images?: any[];
  }
>("repairs/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(`${BaseURL}/api/v1/repairs`, payload);
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || "Failed to create repair");
  }
});

export const fetchRepairsByProperty = createAsyncThunk<
  any,
  { property_id: string; area_type?: string }
>(
  "repairs/fetchByProperty",
  async ({ property_id, area_type }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.get(`${BaseURL}/api/v1/repairs`, {
        params: { property_id, ...(area_type ? { area_type } : {}) },
      });
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch repairs");
    }
  }
);

export const fetchRepairsByClient = createAsyncThunk<
  any,
  {
    client_id: string;
    page?: number;
    limit?: number;
    paginate_by?: "repair" | "estimate";
  }
>(
  "repairs/fetchByClient",
  async (
    { client_id, page = 1, limit = 10, paginate_by },
    { rejectWithValue }
  ) => {
    try {
      const res = await ipponApi.get(`${BaseURL}/api/v1/repairs`, {
        params: {
          client_id,
          page,
          limit,
          ...(paginate_by ? { paginate_by } : {}),
        },
      });
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch repairs by client"
      );
    }
  }
);

export const fetchRepairById = createAsyncThunk<any, { repair_id: string }>(
  "repairs/fetchById",
  async ({ repair_id }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.get(`${BaseURL}/api/v1/repairs/${repair_id}`);
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch repair by id"
      );
    }
  }
);

// Update repair (partial). Supports appending estimate_images and/or repair_images
export const updateRepair = createAsyncThunk<
  any,
  {
    repair_id: string;
    // basic fields
    repair_title?: string;
    area_type?: string; // "共用部" | "専有部"
    area_name?: string;
    status?: string;
    category?: string | string[];
    repair_images?: any[];
    estimate_images?: any[];
    remove_repair_images?: string[];
  }
>("repairs/update", async (payload, { rejectWithValue }) => {
  try {
    const { repair_id, ...body } = payload as any;
    const res = await ipponApi.put(
      `${BaseURL}/api/v1/repairs/${repair_id}`,
      body
    );
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || "Failed to update repair");
  }
});

export const analyzeRepairEstimates = createAsyncThunk<
  any,
  {
    repair_id: string;
    estimate_images: any[];
  }
>("repairs/analyzeRepairEstimates", async (payload, { rejectWithValue }) => {
  try {
    const { repair_id, estimate_images } = payload;
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/repairs/${repair_id}/estimates/analyze`,
      { estimate_images }
    );
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to analyze repair estimate"
    );
  }
});

// Analyze existing S3 estimates without new uploads
export const analyzeExistingRepairEstimates = createAsyncThunk<
  any,
  { repair_id: string }
>("repairs/analyzeExisting", async (payload, { rejectWithValue }) => {
  try {
    const { repair_id } = payload;
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/repairs/${repair_id}/estimates/analyze-existing`
    );
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to analyze existing repair estimates"
    );
  }
});

export const deleteRepairEstimate = createAsyncThunk<
  any,
  {
    repair_id: string;
    repair_estimate_id: string;
  }
>("repairs/deleteRepairEstimate", async (payload, { rejectWithValue }) => {
  try {
    const { repair_id, repair_estimate_id } = payload;
    const res = await ipponApi.delete(
      `${BaseURL}/api/v1/repairs/${repair_id}/estimates/${repair_estimate_id}`
    );
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to delete repair estimate"
    );
  }
});

// Update a single estimate status under a repair
export const updateRepairEstimateStatus = createAsyncThunk<
  any,
  {
    repair_id: string;
    repair_estimate_id: string;
    status?: string;
    estimate_company_name?: string;
    estimate_amount?: number;
  }
>("repairs/updateEstimateStatus", async (payload, { rejectWithValue }) => {
  try {
    const {
      repair_id,
      repair_estimate_id,
      status,
      estimate_company_name,
      estimate_amount,
    } = payload;
    const res = await ipponApi.patch(
      `${BaseURL}/api/v1/repairs/${repair_id}/estimates/${repair_estimate_id}`,
      { status, estimate_company_name, estimate_amount }
    );
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to update estimate status"
    );
  }
});

export const deleteRepair = createAsyncThunk<any, { repair_id: string }>(
  "repairs/delete",
  async (payload, { rejectWithValue }) => {
    try {
      console.log(`🗑️ Deleting repair: ${payload.repair_id}`);
      const res = await ipponApi.delete(
        `${BaseURL}/api/v1/repairs/${payload.repair_id}`
      );
      console.log(`✅ Repair deleted successfully:`, res.data);
      return res.data.data;
    } catch (error: any) {
      console.error("Error message:", error.message);
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to delete repair",
        status: error.response?.status,
        repair_id: payload.repair_id,
        originalError: error.response?.data || error.message,
      });
    }
  }
);

type RepairsState = {
  creating: {
    loading: boolean;
    error: boolean;
    data: any;
  };
  list: {
    loading: boolean;
    error: boolean;
    data: any[];
    total: number;
    page: number;
    limit: number;
  };
  current?: {
    loading: boolean;
    error: boolean;
    data: any | null;
  };
};

const initialState: RepairsState = {
  creating: {
    loading: false,
    error: false,
    data: null,
  },
  list: {
    loading: false,
    error: false,
    data: [],
    total: 0,
    page: 1,
    limit: 10,
  },
  current: {
    loading: false,
    error: false,
    data: null,
  },
};

export const repairsSlice = createSlice({
  name: "repairs",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(createRepair.pending, (state) => {
      state.creating.loading = true;
      state.creating.error = false;
    });
    builder.addCase(createRepair.fulfilled, (state, action) => {
      state.creating.loading = false;
      state.creating.data = action.payload;
    });
    builder.addCase(createRepair.rejected, (state) => {
      state.creating.loading = false;
      state.creating.error = true;
    });

    builder.addCase(fetchRepairsByProperty.pending, (state) => {
      state.list.loading = true;
      state.list.error = false;
    });
    builder.addCase(fetchRepairsByProperty.fulfilled, (state, action) => {
      state.list.loading = false;
      const payload = action.payload;
      const repairs = Array.isArray(payload?.repairs) ? payload.repairs : [];
      state.list.data = repairs;
    });
    builder.addCase(fetchRepairsByProperty.rejected, (state) => {
      state.list.loading = false;
      state.list.error = true;
    });

    builder.addCase(fetchRepairsByClient.pending, (state) => {
      state.list.loading = true;
      state.list.error = false;
    });
    builder.addCase(fetchRepairsByClient.fulfilled, (state, action) => {
      state.list.loading = false;
      const payload = action.payload;
      const repairs = Array.isArray(payload?.repairs) ? payload.repairs : [];
      state.list.data = repairs;
      state.list.total = Number(payload?.total || repairs.length || 0);
      state.list.page = Number(payload?.page || 1);
      state.list.limit = Number(payload?.limit || state.list.limit || 10);
    });
    builder.addCase(fetchRepairsByClient.rejected, (state) => {
      state.list.loading = false;
      state.list.error = true;
    });

    // fetch by id
    builder.addCase(fetchRepairById.pending, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(fetchRepairById.fulfilled, (state, action) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.data = action.payload;
    });
    builder.addCase(fetchRepairById.rejected, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.error = true;
    });

    // updateRepair
    builder.addCase(updateRepair.pending, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(updateRepair.fulfilled, (state, action) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.data = action.payload;
    });
    builder.addCase(updateRepair.rejected, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.error = true;
    });

    // updateRepairEstimateStatus
    builder.addCase(updateRepairEstimateStatus.pending, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(updateRepairEstimateStatus.fulfilled, (state, action) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.data = action.payload;
    });
    builder.addCase(updateRepairEstimateStatus.rejected, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.error = true;
    });

    // analyzeRepairEstimates
    builder.addCase(analyzeRepairEstimates.pending, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(analyzeRepairEstimates.fulfilled, (state, action) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.data = action.payload?.repair || action.payload;
    });
    builder.addCase(analyzeRepairEstimates.rejected, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.error = true;
    });

    // deleteRepairEstimate
    builder.addCase(deleteRepairEstimate.pending, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(deleteRepairEstimate.fulfilled, (state, action) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.data = action.payload;
    });
    builder.addCase(deleteRepairEstimate.rejected, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.error = true;
    });

    // deleteRepair
    builder.addCase(deleteRepair.pending, (state) => {
      state.list.loading = true;
      state.list.error = false;
    });
    builder.addCase(deleteRepair.fulfilled, (state, action) => {
      state.list.loading = false;
      // 削除された修繕をリストから除外
      const deletedRepairId = action.meta.arg.repair_id;
      state.list.data = state.list.data.filter(
        (repair: any) => repair.repair_id !== deletedRepairId
      );
    });
    builder.addCase(deleteRepair.rejected, (state) => {
      state.list.loading = false;
      state.list.error = true;
    });
  },
});

export default repairsSlice.reducer;
